import React from 'react';
import { Link } from 'react-router-dom';
import { Cat, Shield, Scissors, Droplets, ArrowRight } from 'lucide-react';
import './Services.css';

function CatGrooming() {
  return (
    <>
      <section className="page-hero">
        <div className="page-hero__bg">
          <img src="/images/yawning-cat.jpg" alt="Cat grooming" />
          <div className="page-hero__overlay" />
        </div>
        <div className="page-hero__content container">
          <span className="section-label" style={{ color: 'var(--color-accent-light)' }}>Our Services</span>
          <h1 className="page-hero__title">Cat Grooming</h1>
          <p className="page-hero__subtitle">
            Specialist cat grooming with welfare at its core.
          </p>
        </div>
      </section>

      {/* Intro */}
      <section className="services-intro section">
        <div className="container">
          <div className="services-intro__content">
            <p>
              Cat grooming requires a totally different approach to dogs. At Tails &amp; Tweed,
              we provide professional cat grooming delivered with patience, experience, and a deep
              understanding of feline behaviour.
            </p>
            <p>
              We are trained and experienced in handling cats safely and respectfully, always
              prioritising welfare, tolerance and stress reduction.
            </p>
            <p>
              Our environment is calm, structured and managed carefully to ensure cats are groomed
              with minimal disruption. We work within each cat&rsquo;s limits &mdash; never forcing,
              rushing, or compromising welfare for cosmetic results.
            </p>
          </div>
        </div>
      </section>

      {/* Our Approach */}
      <section className="section" style={{ paddingTop: 0 }}>
        <div className="container">
          <div className="services__list">
            <div className="service-detail">
              <div className="service-detail__image">
                <img src="/images/cat-cuddles.jpg" alt="Cat being groomed" />
              </div>
              <div className="service-detail__content">
                <div className="service-detail__icon">
                  <Cat size={28} />
                </div>
                <h2 className="service-detail__title">Our Approach</h2>
                <p className="service-detail__text">
                  Cats are groomed:
                </p>
                <ul className="service-detail__features">
                  <li>With careful handling and controlled environments</li>
                  <li>In short, structured sessions where appropriate</li>
                  <li>According to their temperament and tolerance</li>
                  <li>With honest advice given at every stage</li>
                </ul>
                <p className="service-detail__text" style={{ marginTop: 16 }}>
                  Some cats tolerate our services well. Others require staged appointments or
                  modified plans. We always prioritise what is safest and kindest for your
                  individual cat. Cat grooming appointments can be longer or shorter, and are
                  highly individual.
                </p>
              </div>
            </div>

            <div className="service-detail service-detail--reverse">
              <div className="service-detail__image">
                <img src="/images/cat-lying.jpg" alt="Cat welfare" />
              </div>
              <div className="service-detail__content">
                <div className="service-detail__icon">
                  <Shield size={28} />
                </div>
                <h2 className="service-detail__title">Welfare First &mdash; Always</h2>
                <p className="service-detail__text">
                  We do not:
                </p>
                <ul className="service-detail__features">
                  <li>Sedate</li>
                  <li>Force compliance</li>
                  <li>Push cats beyond their limits</li>
                </ul>
                <p className="service-detail__text" style={{ marginTop: 16 }}>
                  If a cat becomes stressed, we may modify the groom, stop the appointment,
                  or recommend a veterinary supported approach. Your cat&rsquo;s safety and
                  wellbeing will always come before aesthetics.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Cat Services */}
      <section className="section" style={{ background: 'var(--color-cream)' }}>
        <div className="container">
          <div className="text-center" style={{ marginBottom: 48 }}>
            <span className="section-label">What We Offer</span>
            <h2 className="section-title">Cat Services</h2>
          </div>
          <div className="services__list">
            <div className="service-detail">
              <div className="service-detail__image">
                <img src="/images/yawning-cat.jpg" alt="Cat deshed grooming" />
              </div>
              <div className="service-detail__content">
                <div className="service-detail__icon">
                  <Cat size={28} />
                </div>
                <h2 className="service-detail__title">Deshed and Grooming Out</h2>
                <p className="service-detail__text">For cats with tangles or dense undercoats.</p>
                <p className="service-detail__text">We will:</p>
                <ul className="service-detail__features">
                  <li>Assess coat condition honestly</li>
                  <li>Remove loose undercoat</li>
                  <li>Advise on realistic maintenance plans</li>
                </ul>
                <p className="service-detail__note">
                  If matting is present, clipping may be necessary for welfare reasons.
                </p>
              </div>
            </div>

            <div className="service-detail service-detail--reverse">
              <div className="service-detail__image">
                <img src="/images/cat-lying.jpg" alt="Cat belly clip" />
              </div>
              <div className="service-detail__content">
                <div className="service-detail__icon">
                  <Scissors size={28} />
                </div>
                <h2 className="service-detail__title">Belly and Sanitary Clip</h2>
                <p className="service-detail__text">
                  A focused maintenance service to improve hygiene and prevent matting. Includes:
                </p>
                <ul className="service-detail__features">
                  <li>Careful trimming of undercarriage</li>
                  <li>Sanitary area tidy</li>
                  <li>Light detangling if required</li>
                </ul>
                <p className="service-detail__text" style={{ marginTop: 16 }}>Ideal for:</p>
                <ul className="service-detail__features">
                  <li>Long coated breeds</li>
                  <li>Senior cats</li>
                  <li>Cats prone to matting underneath</li>
                </ul>
              </div>
            </div>

            <div className="service-detail">
              <div className="service-detail__image">
                <img src="/images/cat-cuddles.jpg" alt="Lion trim" />
              </div>
              <div className="service-detail__content">
                <div className="service-detail__icon">
                  <Scissors size={28} />
                </div>
                <h2 className="service-detail__title">Lion Trim</h2>
                <p className="service-detail__text">
                  A practical, welfare-led clip for long haired cats who are severely matted,
                  struggling with coat maintenance, or unable to groom themselves effectively.
                  Includes:
                </p>
                <ul className="service-detail__features">
                  <li>Careful clipping of body coat</li>
                  <li>Mane and tail left where possible</li>
                  <li>Hygiene trim</li>
                  <li>Nail trim</li>
                </ul>
                <p className="service-detail__note">
                  Lion trims should never be performed for aesthetic reasons alone &mdash; they are
                  recommended when coat condition or welfare requires intervention.
                </p>
              </div>
            </div>

            <div className="service-detail service-detail--reverse">
              <div className="service-detail__image">
                <img src="/images/yawning-cat.jpg" alt="Cat bathing" />
              </div>
              <div className="service-detail__content">
                <div className="service-detail__icon">
                  <Droplets size={28} />
                </div>
                <h2 className="service-detail__title">Bathing</h2>
                <p className="service-detail__text">
                  Professional bathing can be beneficial for certain coat types and skin conditions.
                  Includes:
                </p>
                <ul className="service-detail__features">
                  <li>Coat assessment</li>
                  <li>Specialist feline safe products</li>
                  <li>Gentle drying process</li>
                  <li>Light brush through</li>
                </ul>
                <p className="service-detail__text" style={{ marginTop: 16 }}>Suitable for:</p>
                <ul className="service-detail__features">
                  <li>Greasy or soiled coats</li>
                  <li>Long coated breeds that require maintenance</li>
                  <li>Coat resets</li>
                </ul>
                <p className="service-detail__note">
                  Wet bathing is only carried out where appropriate and safe for the individual cat.
                  Dry foam bathing is an alternative option for cats where wet bathing is not appropriate.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="page-cta">
        <div className="container">
          <div className="page-cta__inner">
            <div className="page-cta__content">
              <h2>Book Your Cat&rsquo;s Grooming Session</h2>
              <p>Get in touch to discuss your cat&rsquo;s grooming needs.</p>
            </div>
            <div className="page-cta__actions">
              <Link to="/contact" className="btn btn-accent">
                Book Now <ArrowRight size={16} />
              </Link>
              <a href="tel:07904813567" className="btn btn-outline" style={{ borderColor: 'var(--color-white)', color: 'var(--color-white)' }}>
                Call Us
              </a>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

export default CatGrooming;
